import React from 'react';
import { useTranslation } from 'react-i18next';

import { RocketIcon } from '../icons';
import { useCustomizer } from '../../store/customizer';
import {
  Panel,
  Head,
  TitleRow,
  TitleText,
  Bar,
  Seg,
  Steps,
  Row,
  RowHead,
  StepLabel,
  Chip,
  ChipText,
  Desc,
  Summary,
  SummaryLabel,
  SummaryRow,
  FlagImg,
  SummaryValue,
  HintCard,
  HintTitleRow,
  HintLumioMark,
  HintTitle,
  HintBody,
} from './ProgressPanel.styles';

/* The multicolour Lumio mark (same gradient asset as the "Lumio's suggestions"
 * header + the top bar). A gradient image, so a plain <img>, not a tintable glyph. */
const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';

/** Stable English ids — used for the segment `key` and step count. The visible
 *  label/desc copy is translated at render time (see `panelCopy`). */
const PANEL_STEPS: ReadonlyArray<{ label: string; desc: string }> = [
  { label: 'STEP 1', desc: 'Choose the country where the casino will operate.' },
  { label: 'STEP 2', desc: 'Choose website name and web domain.' },
  { label: 'STEP 3', desc: 'Choose logo.' },
];

/**
 * Desktop right-hand "Progress Until Go Live" panel — a 3-segment bar over the
 * three content steps. Completed steps go green ("Done") and COLLAPSE to their
 * selection (the chosen country under step 1; the chosen name & domain under
 * step 2); the active step is orange ("In progress").
 */
export const ProgressPanel = (): React.ReactElement => {
  const { t } = useTranslation();
  const { step, country, selectedName, selectedDomain } = useCustomizer();
  const activeIdx = Math.min(step, PANEL_STEPS.length) - 1;

  // Translated label/desc per step, keyed by the stable English id, so the
  // literals reach the catalog and re-evaluate on language change.
  const panelCopy: Record<string, { label: string; desc: string }> = {
    'STEP 1': { label: t('STEP 1'), desc: t('Choose the country where the casino will operate.') },
    'STEP 2': { label: t('STEP 2'), desc: t('Choose website name and web domain.') },
    'STEP 3': { label: t('STEP 3'), desc: t('Choose logo.') },
  };

  return (
    <Panel>
      <Head>
        <TitleRow>
          <RocketIcon size={24} />
          <TitleText text="Progress Until Go Live" tag="span" />
        </TitleRow>
        <Bar>
          {PANEL_STEPS.map((s, i) => (
            <Seg key={s.label} $on={i <= activeIdx} />
          ))}
        </Bar>
      </Head>
      <Steps>
        {PANEL_STEPS.map((s, i) => {
          const tone = i < activeIdx ? 'done' : i === activeIdx ? 'current' : 'upcoming';
          const showChip = tone === 'done' || tone === 'current';
          // Step 2's selection summary — shown while it's active AND once it's done
          // (done collapses to this instead of repeating the "Choose …" prompt).
          const nameDomainSummary = (
            <Summary>
              <SummaryLabel text={t('Selected name & domain')} tag="span" />
              <SummaryValue text={selectedName ?? t('Not selected')} tag="span" $muted={!selectedName} />
              <SummaryValue text={selectedDomain ?? t('Not selected')} tag="span" $muted={!selectedDomain} />
            </Summary>
          );
          return (
            <Row key={s.label}>
              <RowHead>
                <StepLabel text={panelCopy[s.label].label} tag="span" />
                {showChip ? (
                  <Chip $tone={tone === 'done' ? 'done' : 'current'}>
                    <ChipText text={tone === 'done' ? t('Done') : t('In progress')} tag="span" $tone={tone === 'done' ? 'done' : 'current'} />
                  </Chip>
                ) : null}
              </RowHead>

              {/* Done steps COLLAPSE to their selection (step 1 → country, step 2 →
                  name & domain); active/upcoming steps show the "Choose …" prompt. */}
              {i === 0 && tone === 'done' && country ? (
                <Summary>
                  <SummaryLabel text={t('Operating Country')} tag="span" />
                  <SummaryRow>
                    {country.flag ? <FlagImg src={country.flag} alt="" /> : null}
                    <SummaryValue text={country.name} tag="span" />
                  </SummaryRow>
                </Summary>
              ) : i === 1 && tone === 'done' ? (
                nameDomainSummary
              ) : (
                <Desc text={panelCopy[s.label].desc} tag="paragraph" $tone={tone} />
              )}

              {/* STEP 2 while active: ALSO show the live name & domain under the prompt. */}
              {i === 1 && tone === 'current' ? nameDomainSummary : null}

              {/* STEP 3 while active: the "you can change the logo later" hint. */}
              {i === 2 && tone === 'current' ? (
                <HintCard>
                  <HintTitleRow>
                    <HintLumioMark src={LUMIO_MARK_SRC} alt="" />
                    <HintTitle text="Just a heads up!" tag="span" />
                  </HintTitleRow>
                  <HintBody
                    text="You can change the logo within 14 days after going live, or until you hit 50 registered users."
                    tag="paragraph"
                  />
                </HintCard>
              ) : null}
            </Row>
          );
        })}
      </Steps>
    </Panel>
  );
};

export default ProgressPanel;